/**
 * PhotoPulseQueue — /publisher/photopulse-queue
 *
 * Distraction-free moderation dashboard. Lists queued submissions with
 * a preview image, metadata, and Approve / Reject buttons.
 *
 * When PHOTOPULSE_ACTIVE flips on in the backend, approved photos
 * become instantly browsable via /api/publisher/v1/photopulse/search.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Loader2, Check, X, ImageIcon, MapPin, Tag, Camera, AlertCircle,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

export default function PhotoPulseQueue() {
  const [tab, setTab] = useState("queued"); // queued | approved | rejected
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/photopulse/queue?status=${tab}&limit=50`);
      const d = await r.json();
      setRows(d.submissions || []);
      setActive(!!d.active);
    } catch { /* noop */ }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const moderate = useCallback(async (sub, action) => {
    setBusyId(sub.submission_id);
    try {
      await fetch(`${BASE}/photopulse/queue/${sub.submission_id}/moderate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      load();
    } catch { /* noop */ }
    setBusyId(null);
  }, [load]);

  return (
    <div
      className="min-h-screen"
      style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="photopulse-queue-page"
    >
      <header className="max-w-[1200px] mx-auto px-6 md:px-10 pt-8 pb-4">
        <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2">
          PhotoPulse · Moderation
        </p>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "2.2rem", letterSpacing: "-0.02em" }}>
            Photo submissions
          </h1>
          <span
            className={`text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full ${
              active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}
            data-testid="photopulse-queue-flag"
          >
            {active ? "Library live" : "Library warming up"}
          </span>
        </div>
        {!active && (
          <div
            className="mt-4 flex items-start gap-2 text-xs bg-amber-50 text-amber-800 px-3 py-2 rounded-lg"
            data-testid="photopulse-queue-flag-note"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>PHOTOPULSE_ACTIVE=false</strong> in backend .env — approved photos are
              being staged. Flip the env flag to serve the community library from{" "}
              <code>/api/publisher/v1/photopulse/search</code>.
            </span>
          </div>
        )}
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 border-b border-slate-200 flex gap-2 text-sm mb-6">
        {["queued", "approved", "rejected"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-3 border-b-2 -mb-px font-semibold transition-colors ${
              tab === t
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
            data-testid={`photopulse-queue-tab-${t}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mx-auto mb-4">
              <Camera className="w-7 h-7 text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">
              No {tab} submissions right now.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((s) => (
              <div
                key={s.submission_id}
                className="bg-white rounded-2xl border overflow-hidden"
                style={{ borderColor: "rgba(15,23,42,0.08)" }}
                data-testid={`photopulse-queue-row-${s.submission_id}`}
              >
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  {s.image_url ? (
                    <img src={s.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-300">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900">{s.title || "Untitled submission"}</h3>
                  {s.description && (
                    <p className="mt-1 text-sm text-slate-600 line-clamp-2">{s.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {s.place && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {s.place}
                      </span>
                    )}
                    {s.topic && (
                      <span className="inline-flex items-center gap-1">
                        <Tag className="w-3 h-3" /> {s.topic}
                      </span>
                    )}
                    <span className="text-slate-400">{s.license}</span>
                  </div>
                  {s.photographer?.name && (
                    <div className="mt-2 text-xs text-slate-500">
                      Photo by <strong className="text-slate-700">{s.photographer.name}</strong>
                    </div>
                  )}
                  {tab === "queued" && (
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => moderate(s, "approve")}
                        disabled={busyId === s.submission_id}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-full text-white text-xs font-semibold disabled:opacity-40"
                        style={{ background: "#059669" }}
                        data-testid={`photopulse-queue-approve-${s.submission_id}`}
                      >
                        {busyId === s.submission_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => moderate(s, "reject")}
                        disabled={busyId === s.submission_id}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200 disabled:opacity-40"
                        data-testid={`photopulse-queue-reject-${s.submission_id}`}
                      >
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  )}
                  {tab !== "queued" && (
                    <div className="mt-3 text-[10px] uppercase tracking-widest font-semibold text-slate-400">
                      {tab === "approved" ? "Approved" : "Rejected"}
                      {s.moderated_at && <> · {new Date(s.moderated_at).toLocaleDateString()}</>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
